import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";
import Toast from "../../components/common/toast/Toast";
import ToggleButton from "../../components/common/inputs/toggles/Toggle";
import { BasicWeightManagement, ErrorValidation } from "../../types/types";
import DeleteButton from "../../components/common/buttons/DeleteButton";
import {
  createBaggages,
  deleteBaggage,
  getBaggages,
} from "../../service/baggageService";

interface EnhancedBaggageManagement extends BasicWeightManagement {
  adultValue: string;
  childValue: string;
  infantValue: string;
}

const Luggage: React.FC = () => {
  const [showToast, setShowToast] = useState<boolean>(false);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");
  const [toastType, setToastType] = useState<"success" | "error" | "warning">(
    "success"
  );
  const [luggageList, setLuggageList] = useState<EnhancedBaggageManagement[]>(
    []
  );
  const [allAirLine, setAllAirLine] = useState<boolean>(false);
  const [luggage, setLuggage] = useState<EnhancedBaggageManagement>({
    id: 0,
    departure: "",
    destination: "",
    airLine: "",
    value: "", // This will be deprecated
    adultValue: "",
    childValue: "",
    infantValue: "",
    isPieces: false,
  });

  const location = useLocation();

  useEffect(() => {
    fetchLuggages();
    if (location.state) {
      setLuggage({
        ...location.state,
        value: "",
        adultValue: "",
        childValue: "",
        infantValue: "",
      });
    }
  }, [location]);

  const fetchLuggages = async () => {
    setIsDataLoading(true);
    try {
      const luggages = await getBaggages();
      // Ensure each luggage item has all required fields
      const processedLuggages = luggages.map((luggage: any) => ({
        ...luggage,
        adultValue: luggage.adultValue || luggage.value || "",
        childValue: luggage.childValue || luggage.value || "",
        infantValue: luggage.infantValue || luggage.value || "",
        value: luggage.value || "",
      }));
      setLuggageList(processedLuggages);
    } catch (error) {
      console.error("Error fetching luggages:", error);
      setToastMessage("Failed to fetch baggage details");
      setShowToast(true);
      setToastType("error");
    }
    setIsDataLoading(false);
  };

  const validateForm = (): ErrorValidation => {
    const textRegex = /^[A-Za-z\s]+$/;
    const {
      departure,
      destination,
      airLine,
      adultValue,
      childValue,
      infantValue,
    } = luggage;

    for (const existingLuggage of luggageList) {
      if (
        existingLuggage.departure.toLowerCase().trim() ===
          departure.toLowerCase().trim() &&
        existingLuggage.destination.toLowerCase().trim() ===
          destination.toLowerCase().trim() &&
        existingLuggage.airLine.toLowerCase().trim() ===
          airLine.toLowerCase().trim()
      ) {
        return {
          message: "Baggage value already added for this route.",
          validate: false,
        };
      }
    }

    if (
      departure.trim() === "" ||
      destination.trim() === "" ||
      airLine.trim() === ""
    ) {
      return {
        message: "All fields are required.",
        validate: false,
      };
    }

    if (!textRegex.test(departure.trim())) {
      return {
        message: "Departure should contain only letters.",
        validate: false,
      };
    }

    if (!textRegex.test(destination.trim())) {
      return {
        message: "Destination should contain only letters.",
        validate: false,
      };
    }

    if (departure.toLowerCase().trim() === destination.toLowerCase().trim()) {
      return {
        message: "Departure and destination cannot be the same.",
        validate: false,
      };
    }

    if (
      adultValue.trim() === "" &&
      childValue.trim() === "" &&
      infantValue.trim() === ""
    ) {
      return {
        message: "At least one baggage value is required.",
        validate: false,
      };
    }

    return {
      message: "",
      validate: true,
    };
  };

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLuggage((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAllAirLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAllAirLine(e.target.checked);
    if (!e.target.checked) {
      setLuggage((prev) => ({
        ...prev,
        airLine: "",
      }));
    } else {
      setLuggage((prev) => ({
        ...prev,
        airLine: "All",
      }));
    }
  };

  const handleToggleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLuggage((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked,
    }));
  };

  const handleUpdate = async () => {
    const validateInfo = validateForm();
    if (validateInfo.validate === false) {
      setToastMessage(validateInfo.message);
      setShowToast(true);
      setToastType("error");
      return;
    }

    setIsDataLoading(true);

    // Prepare the data to send to backend
    const dataToSend = {
      ...luggage,
      adultValue: Number(luggage.adultValue) || 0,
      childValue: Number(luggage.childValue) || 0,
      infantValue: Number(luggage.infantValue) || 0,
    };

    try {
      const response = await createBaggages(dataToSend);

      if (response && response.success) {
        // Fetch fresh data from server to ensure sync
        await fetchLuggages();

        // Reset the form
        setLuggage({
          id: 0,
          departure: "",
          destination: "",
          airLine: "",
          value: "",
          adultValue: "",
          childValue: "",
          infantValue: "",
          isPieces: false,
        });
        setAllAirLine(false);

        setShowToast(true);
        setToastMessage("Baggage added successfully!");
        setToastType("success");
      } else {
        setShowToast(true);
        setToastMessage(response?.message || "Failed to add baggage");
        setToastType("error");
      }
    } catch (error) {
      console.error("Error creating baggage:", error);
      setShowToast(true);
      setToastMessage("Failed to add baggage");
      setToastType("error");
    }

    setIsDataLoading(false);
  };

  const handleDelete = async (id: number) => {
    setIsDataLoading(true);
    const response = await deleteBaggage(id);
    setLuggageList((prev) => prev.filter((luggage) => luggage.id !== id));
    setIsDataLoading(false);
  };

  return (
    <div className="container mx-auto p-6">
      {showToast && (
        <Toast
          type={toastType}
          onClose={() => setShowToast(false)}
          message={toastMessage}
        />
      )}
      {isDataLoading && <LoadingIndicator></LoadingIndicator>}
      <h2 className="text-3xl font-bold mb-6">Baggage Settings</h2>

      <div className="">
        <div className="">
          <div className="overflow-x-auto bg-white shadow-md rounded-lg overflow-hidden mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Departure
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Destination
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Air Line
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-nowrap">
                    All Air Lines
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Adult Baggage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Child Baggage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Infant Baggage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                <tr className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={3}
                      type="text"
                      name="departure"
                      value={luggage.departure.toUpperCase()}
                      className="border border-gray-300 rounded p-2 w-20"
                      onChange={handleValueChange}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={3}
                      type="text"
                      name="destination"
                      value={luggage.destination.toUpperCase()}
                      className="border border-gray-300 rounded p-2 w-20"
                      onChange={handleValueChange}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      maxLength={allAirLine ? 3 : 2}
                      type="text"
                      name="airLine"
                      value={allAirLine ? "ALL" : luggage.airLine.toUpperCase()}
                      className="border border-gray-300 rounded p-2 w-20"
                      onChange={handleValueChange}
                      disabled={allAirLine}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <ToggleButton
                      name="isAllAirlines"
                      value={allAirLine}
                      onValueChange={handleAllAirLineChange}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        name="adultValue"
                        value={luggage.adultValue}
                        placeholder="Adult"
                        className="border border-gray-300 rounded p-2 w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onChange={handleValueChange}
                      />
                      <div className="font-bold text-blue-300 text-xs">
                        {luggage.isPieces ? "P" : "Kg"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        name="childValue"
                        value={luggage.childValue}
                        placeholder="Child"
                        className="border border-gray-300 rounded p-2 w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onChange={handleValueChange}
                      />
                      <div className="font-bold text-blue-300 text-xs">
                        {luggage.isPieces ? "P" : "Kg"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        name="infantValue"
                        value={luggage.infantValue}
                        placeholder="Infant"
                        className="border border-gray-300 rounded p-2 w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        onChange={handleValueChange}
                      />
                      <div className="font-bold text-blue-300 text-xs">
                        {luggage.isPieces ? "P" : "Kg"}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">
                        {luggage.isPieces ? "Pieces" : "Weight"}
                      </span>
                      <ToggleButton
                        name="isPieces"
                        value={luggage.isPieces}
                        onValueChange={handleToggleValueChange}
                      />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 text-right">
            <button
              className={`bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-900`}
              onClick={handleUpdate}
            >
              Add
            </button>
          </div>
          <div className="overflow-x-auto mt-6 bg-white shadow-md rounded-lg overflow-hidden mb-6">
            {luggageList.length == 0 ? (
              <div className="p-2 flex justify-center text-sm text-gray-400">
                No baggage details found.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Departure
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destination
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Air Line
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Adult Baggage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Child Baggage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Infant Baggage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Settings
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {luggageList.map((luggage) => (
                    <tr key={luggage.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {luggage.departure.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {luggage.destination.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {luggage.airLine.toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {luggage.adultValue || luggage.value}{" "}
                        {luggage.isPieces ? (
                          <span>piece(s)</span>
                        ) : (
                          <span>Kg</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {luggage.childValue || luggage.value}{" "}
                        {luggage.isPieces ? (
                          <span>piece(s)</span>
                        ) : (
                          <span>Kg</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {luggage.infantValue || luggage.value}{" "}
                        {luggage.isPieces ? (
                          <span>piece(s)</span>
                        ) : (
                          <span>Kg</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DeleteButton
                          onConfirm={() => {
                            handleDelete(luggage.id);
                          }}
                        ></DeleteButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Luggage;
